/**
 * Small DOM and formatting helpers for the quiz.
 *
 * @package ppd
 * @since   1.0.27
 */

/**
 * Create an element.
 *
 * @param {string} tag
 * @param {Object} [attrs]    Attributes; `class` and `text` are shortcuts.
 * @param {...Node} children
 * @return {HTMLElement}
 */
export function el(tag, attrs = {}, ...children) {
	const node = document.createElement(tag);
	Object.keys(attrs).forEach((key) => {
		const value = attrs[key];
		if (value === null || value === undefined || value === false) {
			return;
		}
		if (key === 'class') {
			node.className = value;
		} else if (key === 'text') {
			node.textContent = value;
		} else {
			node.setAttribute(key, value === true ? '' : value);
		}
	});
	children.filter(Boolean).forEach((child) => node.append(child));
	return node;
}

/**
 * Minimal sprintf for "%s", "%d" and positional "%1$d" placeholders.
 *
 * @param {string} template
 * @param {...*}   values
 * @return {string}
 */
export function format(template, ...values) {
	let next = 0;
	return template.replace(/%(?:(\d+)\$)?[sd]/g, (match, position) => {
		const index = position ? Number(position) - 1 : next++;
		return String(values[index]);
	});
}

/**
 * @return {boolean}
 */
export function prefersReducedMotion() {
	return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Random ID. crypto.randomUUID() needs a secure context, so fall back to
 * getRandomValues() (available on http:// too).
 *
 * @return {string}
 */
function randomId() {
	if (window.crypto && window.crypto.randomUUID) {
		return window.crypto.randomUUID();
	}
	const bytes = new Uint8Array(16);
	window.crypto.getRandomValues(bytes);
	const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
	return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/**
 * Session ID for a quiz, kept for the life of the browser tab so repeat runs
 * by one visitor can be grouped in HubSpot.
 *
 * @param {number} quizId
 * @return {string}
 */
export function getSessionId(quizId) {
	const key = `emQuizSession:${quizId}`;
	try {
		const existing = window.sessionStorage.getItem(key);
		if (existing) {
			return existing;
		}
	} catch (e) {
		// Storage unavailable; use a per-page ID.
	}
	const id = randomId();
	try {
		window.sessionStorage.setItem(key, id);
	} catch (e) {
		// Ignore.
	}
	return id;
}

/**
 * Resolve once `test()` returns truthy, polling every 100ms.
 *
 * @param {Function} test
 * @param {number}   [timeout] Milliseconds before rejecting.
 * @return {Promise}
 */
export function waitFor(test, timeout = 15000) {
	return new Promise((resolve, reject) => {
		const started = Date.now();
		const check = () => {
			const value = test();
			if (value) {
				resolve(value);
			} else if (Date.now() - started > timeout) {
				reject(new Error('Timed out'));
			} else {
				setTimeout(check, 100);
			}
		};
		check();
	});
}
